import React, { useEffect, useRef, useState } from 'react'

import { HighlightedText } from '@features/timelineSidebar/components/filters/HighlightedText'
import HYPER_LINK from '@public/assets/hyperlink_open.svg'

import {
  WebResearchQueryPopover,
  useDelayedHover,
} from './WebResearchQueryCard'
import {
  formatWebActivityLabel,
  getWebActivityDomain,
  getWebActivityTerms,
  getWebActivityUrl,
  type WebActivityLabelMode,
  type WebActivityRecord,
} from './webActivityQuery'

interface WebActivityNotesProps {
  highlightQuery?: string
  isMobile?: boolean
  item: WebActivityRecord
  mode: WebActivityLabelMode
  muted?: boolean
}

export function WebActivityNotes({
  highlightQuery = '',
  isMobile = false,
  item,
  mode,
  muted = false,
}: WebActivityNotesProps) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [mobileCardOpen, setMobileCardOpen] = useState(false)
  const [rowHovered, setRowHovered] = useState(false)
  const label = formatWebActivityLabel(item, mode)
  const terms = getWebActivityTerms(item, mode)
  const url = getWebActivityUrl(item)
  const showCard = terms.length > 0
  const hover = useDelayedHover(!isMobile && showCard)

  useEffect(() => {
    if (!isMobile || !mobileCardOpen) return

    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (anchorRef.current?.contains(target)) return
      if (
        target instanceof Element &&
        target.closest('.web-query-card, .web-query-popup')
      ) {
        return
      }
      setMobileCardOpen(false)
    }

    document.addEventListener('mousedown', closeOnOutsideClick)
    return () => document.removeEventListener('mousedown', closeOnOutsideClick)
  }, [isMobile, mobileCardOpen])

  if (!label) return null

  const openLink = () => {
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleTextClick = (event: React.MouseEvent) => {
    if (isMobile && showCard) {
      event.stopPropagation()
      setMobileCardOpen(open => !open)
      return
    }
    openLink()
  }

  const cardOpen = isMobile ? mobileCardOpen : hover.open

  return (
    <div
      ref={anchorRef}
      className="flex w-full min-w-0 items-center"
      onMouseEnter={() => {
        setRowHovered(true)
        hover.onMouseEnter()
      }}
      onMouseLeave={() => {
        setRowHovered(false)
        hover.onMouseLeave()
      }}
    >
      <div
        className={`min-w-0 flex-1 truncate text-sm font-normal ${
          url ? 'clickable-sysnotes' : ''
        } ${url || (isMobile && showCard) ? 'cursor-pointer' : 'cursor-default'}`}
        style={{
          color: muted ? '#999' : '#525252',
          textDecoration: cardOpen ? 'underline' : undefined,
        }}
        onClick={handleTextClick}
      >
        <HighlightedText query={highlightQuery} text={label} />
      </div>

      {url && (isMobile || rowHovered || cardOpen) && (
        <a
          aria-label="Open link"
          className="ml-1 inline-flex shrink-0 items-center no-underline"
          href={url}
          rel="noopener noreferrer"
          target="_blank"
          onClick={event => event.stopPropagation()}
        >
          <img alt="" height={16} src={HYPER_LINK} width={16} />
        </a>
      )}

      <WebResearchQueryPopover
        anchor={anchorRef.current}
        domain={getWebActivityDomain(item)}
        open={cardOpen}
        terms={terms}
        url={url}
        onMouseEnter={hover.onMouseEnter}
        onMouseLeave={hover.onMouseLeave}
      />
    </div>
  )
}
